/**
 * A strategy's Risk policy, and — for the platform publisher — the one way to
 * change it: a new immutable revision, shown as before → after, published only
 * when the publisher presses «Опубликовать».
 *
 * The policy is relative to the slot margin, so the form asks for a multiple
 * and a margin of safety, never for a sum: positions grow with the account.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { draftPlatformRevision, publishPlatformRevision } from "@/shared/api";
import { fromPercent, shifted, toPlaces } from "@/shared/money/money";
import { integerParameter } from "@/shared/parameters/integer-parameter";
import type { RegistryRevisionResponse, RegistryStrategyResponse } from "@/shared/api";

import { policyText } from "./riskPolicy";

/** The owner-approved values, offered when a version declares none yet. */
const APPROVED = { multiple: "2", bufferPercent: "15", ageSeconds: "60" } as const;

const LABEL: Readonly<Record<string, string>> = {
  "risk_policy.slot_exposure_multiple": "Позиция на слот, × маржи слота",
  "risk_policy.liquidation_safety_buffer": "Запас до ликвидации (доля)",
  "risk_policy.maximum_market_data_age_seconds": "Возраст рыночных данных, с",
};

/** Whole seconds the server accepts for data freshness. */
const AGES: readonly string[] = Array.from({ length: 300 }, (_, index) => String(index + 1));

const FIELD =
  "min-h-9 w-28 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

function field(raw: unknown, fallback: string): string {
  return typeof raw === "string" ? raw : typeof raw === "number" ? String(raw) : fallback;
}

export function RiskPolicySection({ version, canRevise, registryKey }: {
  readonly version: RegistryStrategyResponse;
  readonly canRevise: boolean;
  readonly registryKey: readonly unknown[];
}): React.JSX.Element {
  const queries = useQueryClient();
  const current = version.risk_policy ?? null;
  const [editing, setEditing] = useState(false);
  const [multiple, setMultiple] = useState<string>(field(current?.slot_exposure_multiple, APPROVED.multiple));
  const [bufferPercent, setBufferPercent] = useState<string>(
    typeof current?.liquidation_safety_buffer === "string"
      ? toPlaces(shifted(current.liquidation_safety_buffer, 2), 0)
      : APPROVED.bufferPercent,
  );
  const [age, setAge] = useState<string>(field(current?.maximum_market_data_age_seconds, APPROVED.ageSeconds));
  const [draft, setDraft] = useState<RegistryRevisionResponse | null>(null);
  // The stored rate is sent back untouched unless the field was edited: the
  // whole-point display would otherwise round "0.155" and change it silently.
  const [bufferEdited, setBufferEdited] = useState(false);
  const storedBuffer = typeof current?.liquidation_safety_buffer === "string" ? current.liquidation_safety_buffer : null;

  const prepare = useMutation({
    mutationFn: async () => {
      const { data } = await draftPlatformRevision({
        path: { version_id: version.version_id },
        body: { risk_policy: {
          kind: "relative-v1",
          slot_exposure_multiple: multiple,
          liquidation_safety_buffer: !bufferEdited && storedBuffer !== null ? storedBuffer : fromPercent(bufferPercent),
          maximum_market_data_age_seconds: integerParameter(age.trim(), AGES),
        } },
      });
      if (data === undefined) throw new Error("revision refused");
      return data;
    },
    onSuccess: setDraft,
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (draft === null) throw new Error("no draft");
      const { data } = await publishPlatformRevision({ path: { version_id: draft.version_id } });
      if (data === undefined) throw new Error("publication refused");
    },
    onSuccess: async () => {
      setDraft(null);
      setEditing(false);
      await queries.invalidateQueries({ queryKey: registryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2 border-t border-border-subtle pt-3" data-testid={`risk-policy-${version.strategy_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-body-main text-text-secondary">Лимиты риска</h3>
        {canRevise && !editing && (
          <Button onClick={() => { setEditing(true); }}>{current === null ? "Задать лимиты" : "Изменить"}</Button>
        )}
      </div>
      <p className={`text-body-main ${current === null ? "text-status-warning" : "text-text-primary"}`}>
        {current === null ? "Не заданы — стратегию нельзя назначить на счёт." : policyText(current)}
      </p>
      {canRevise && editing && draft === null && (
        <form className="flex flex-col gap-2" onSubmit={(event) => { event.preventDefault(); if (!prepare.isPending) prepare.mutate(); }}>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-helper-sm">Позиция на слот, × маржи</span>
              <input aria-label="Позиция на слот, × маржи" className={FIELD} inputMode="decimal" required value={multiple} onChange={(event) => { setMultiple(event.target.value); }} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-helper-sm">Запас до ликвидации, п.п.</span>
              <input aria-label="Запас до ликвидации, п.п." className={FIELD} inputMode="decimal" required value={bufferPercent} onChange={(event) => { setBufferPercent(event.target.value); setBufferEdited(true); }} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-helper-sm">Данные не старше, с</span>
              <input aria-label="Данные не старше, с" className={FIELD} inputMode="numeric" required value={age} onChange={(event) => { setAge(event.target.value); }} />
            </label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={prepare.isPending}>Показать изменения</Button>
            <Button onClick={() => { setEditing(false); }}>Отмена</Button>
          </div>
        </form>
      )}
      {draft !== null && (
        <div className="flex flex-col gap-2" data-testid="revision-diff">
          <p className="text-body-main text-text-primary">Новая ревизия {version.strategy_id}: было → стало</p>
          <dl className="divide-y divide-border-subtle text-body-main">
            {draft.diffs.filter((item) => item.parameter_id in LABEL).map((item) => (
              <div key={item.parameter_id} className="flex flex-wrap items-baseline justify-between gap-3 py-1.5">
                <dt className="text-text-secondary">{LABEL[item.parameter_id]}</dt>
                <dd className="tabular-nums text-text-primary">{item.before === "not set" ? "—" : item.before} → {item.after}</dd>
              </div>
            ))}
          </dl>
          <p className="text-helper-sm">Публикуется новая неизменяемая версия; прежняя остаётся в истории, открытые циклы её не меняют.</p>
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => { publish.mutate(); }} disabled={publish.isPending}>Опубликовать</Button>
            <Button onClick={() => { setDraft(null); }}>Изменить</Button>
          </div>
        </div>
      )}
      {(prepare.isError || publish.isError) && (
        <p role="alert" className="text-status-warning">Сервер отклонил значения. Проверьте: позиция на слот не больше плеча, запас 1–99 п.п., данные 1–300 с.</p>
      )}
    </section>
  );
}
